<template>
	<div
		id="settingsWrapper"
		ref="settingswrapper"
		:class="{
			'announcements-banner-settings-open':
				store.showAnnouncementsBanner || store.askForNotificationPermission,
			'announcements-banner-settings-close':
				!store.showAnnouncementsBanner && !store.askForNotificationPermission,
		}"
	>
		<CloseIcon
			hover
			class="close-settings"
			@click="goBack"
			:aria-label="$t('common_button_close')"
			tabindex="0"
			type="regular"
			role="button"
		/>

		<div class="settings-list noSelect">
			<RouterLink :to="{ name: 'mainprofile' }" class="profile-section">
				<div class="profile-container">
					<ProfilePic
						:user="store.auth.user"
						size="75px"
						class="profilepic"
						:edit_icon="true"
					/>
					<div class="profile-name" v-text="store.auth.user.fullname" />
					<div v-if="showPronoun" class="user-pronoun-container">
						<div class="user-pronoun">{{ store.auth.user.pronouns }}</div>
					</div>
					<div v-if="showTitle" class="user-title">
						{{ store.auth.user.title }}
					</div>
					<div class="organization-name" v-text="org.name" />
				</div>
			</RouterLink>

			<div class="main-settings-nav-item no-hover">
				<div class="main-settings-nav-content">
					<MoonIcon
						type="solid"
						class="main-settings-nav-icon"
						:class="{ active: store.auth.user.mute }"
					/>
					<div
						class="main-settings-nav-label"
						v-t="'settings_option_donotdisturb'"
					/>
				</div>
				<div class="dnd-controls" :class="{ active: store.auth.user.mute }">
					<div
						class="dnd-state"
						@click="showDnDModal = true"
						data-cy="dndUntil"
						v-text="dndUntil"
					/>
					<ToggleSwitch v-model="dndChecked" />
				</div>
			</div>

			<component
				v-bind="item.props"
				class="main-settings-nav-item"
				v-for="item in navItems"
				:key="item.id"
				:is="item.tag"
				:class="item.class || {}"
				@click="item.onClick && item.onClick()"
			>
				<div class="main-settings-nav-content">
					<component
						v-bind="item.iconProps || {}"
						:is="item.icon"
						class="main-settings-nav-icon"
					/>
					<div class="main-settings-nav-label" v-t="item.label" />
				</div>
				<ChevronIcon class="main-settings-nav-chevron" right />
			</component>
		</div>

		<div id="divLogOut">
			<Button danger large @click="logout()">{{
				$t('settings_button_logout')
			}}</Button>
		</div>

		<div id="divVersion">
			<ProntoNameLogoIcon />
			<div class="build-number">Version: {{ buildNumber }}</div>
		</div>

		<MuteModal
			v-if="showDnDModal"
			:edit="store.auth.user.mute"
			@close="closeDnDModal"
		/>
	</div>
</template>

<script>
import store from '@/store'
import { post } from '@/helpers/api'
import {
	ProfilePic,
	Button,
	StandardModal,
	ToggleSwitch,
} from '@/components/common'
import {} from '@/icons'
import MuteModal from '@/components/MuteModal'
import moment from 'moment'
import { fetchMyUserInfo } from '@/api/user'
import {
	PenToSquareIcon,
	UsersGearIcon,
	MoonIcon,
	BugIcon,
	BellIcon,
	EarthIcon,
	EyeSlashIcon,
	UserSlashIcon,
	FileLinesIcon,
	CoinsIcon,
	ChevronIcon,
	TrashCanIcon,
	CloseIcon,
	ProntoNameLogoIcon,
	GuageHighIcon,
	CircleHalfStrokeIcon,
	WebhookIcon,
} from '@/icons'
import i18n from '@/i18n'
import { redirectToLastActiveGroup } from '@/helperMethods'
import { canManageWebhooks } from '@/helpers/permissions'
import { getLocalDate } from '@/helpers/utils'
import { USER_TITLE, USER_PRONOUNS, orgFlag } from '@/helpers/flags.js'

export default {
	components: {
		ProfilePic,
		PenToSquareIcon,
		UsersGearIcon,
		MoonIcon,
		BugIcon,
		BellIcon,
		EarthIcon,
		EyeSlashIcon,
		UserSlashIcon,
		FileLinesIcon,
		ToggleSwitch,
		MuteModal,
		CoinsIcon,
		Button,
		ChevronIcon,
		StandardModal,
		TrashCanIcon,
		CloseIcon,
		ProntoNameLogoIcon,
		GuageHighIcon,
		CircleHalfStrokeIcon,
		WebhookIcon,
	},
	data() {
		return {
			store,
			disabled: true,
			initialLoad: false,
			isUpdating: false,
			showLoader: false,
			isAdmin: false,
			dndChecked: store.auth.user.mute,
			showDnDModal: false,
			buildNumber: window.appConfig.buildNumber,
			org: {},
		}
	},

	created() {
		this.hasAdminAccess()
		this.org = store.auth.user.organizations[0]

		if (this.$route.query.s === 'profile') {
			this.$router.replace({ name: 'mainprofile' })
		}

		this.$onEventBus('OrgUpdated', () => {
			this.org = store.auth.user.organizations[0]
		})

		fetchMyUserInfo(true)
	},

	mounted() {
		this.$nextTick(function () {
			this.initialLoad = true
		})
	},

	watch: {
		'store.auth.user.mute': {
			handler(val) {
				this.dndChecked = val
				this.showDnDModal = false
			},
			deep: true,
		},

		dndChecked(checked) {
			if (checked === false && store.auth.user.mute) {
				this.turnOffDnd()
			} else {
				this.showDnDModal = true
			}
		},
	},
	computed: {
		showTitle() {
			return orgFlag(USER_TITLE) && store.auth.user?.title
		},

		showPronoun() {
			return orgFlag(USER_PRONOUNS) && store.auth.user?.pronouns
		},

		canCreateTokens() {
			const { user } = store.auth
			const { integrations_enabled } = user.organizations[0]

			return (
				integrations_enabled &&
				this.store.checkOrgPermission('create_api_tokens')
			)
		},

		canManageWebhooks() {
			return canManageWebhooks()
		},

		dndUntil() {
			if (!this.store.auth.user.mute)
				return i18n.global.t('settings_notifications_subtitle_donotdisturb')
			if (!this.store.auth.user.muteuntil)
				return i18n.global.t(
					'groupsettings_notifications_mute_action_indefinite'
				)

			const midnightTonight = moment().endOf('day')
			const dndMoment = moment(getLocalDate(store.auth.user.muteuntil))

			if (dndMoment.diff(midnightTonight, 'days') > 1) {
				return i18n.global.t(
					'groupsettings_notifications_mute_action_indefinite'
				)
			} else {
				return i18n.global.t('groupsettings_notifications_mute_expiry', [
					dndMoment.format('LT'),
				])
			}
		},

		hasBlockedUsers() {
			return store.auth.user.blocked?.length
		},

		navItems() {
			const items = [
				{
					id: 'notif',
					tag: 'router-link',
					label: 'settings_option_notifications',
					icon: BellIcon,
					iconProps: { type: 'solid' },
					props: { to: { name: 'mainnotifications' } },
				},
				{
					id: 'lang',
					tag: 'router-link',
					label: 'settings_option_translation',
					icon: EarthIcon,
					props: { to: { name: 'language' } },
				},
				{
					id: 'appearance',
					tag: 'router-link',
					label: 'settings_option_appearance',
					icon: CircleHalfStrokeIcon,
					iconProps: { type: 'regular' },
					props: { to: { name: 'appearance' } },
				},
				{
					id: 'hidden',
					tag: 'router-link',
					label: 'settings_option_hidden_groups',
					icon: EyeSlashIcon,
					iconProps: { type: 'solid' },
					props: { to: { name: 'hiddengroups' } },
				},
			]
			if (this.hasBlockedUsers) {
				items.push({
					id: 'blocked',
					tag: 'router-link',
					label: 'settings_option_blockedusers',
					icon: UserSlashIcon,
					iconProps: { type: 'solid' },
					props: { to: { name: 'blockedusers' } },
				})
			}
			if (this.canCreateTokens) {
				items.push({
					id: 'tokens',
					tag: 'router-link',
					label: 'settings_option_api_tokens',
					icon: CoinsIcon,
					iconProps: { type: 'solid' },
					props: { to: { name: 'apitokens' } },
				})
			}
			if (this.canManageWebhooks) {
				items.push({
					id: 'webhooks',
					tag: 'router-link',
					label: 'settings_option_webhooks',
					icon: WebhookIcon,
					iconProps: { type: 'solid' },
					class: { 'nav-item-webhooks': true },
					props: { to: { name: 'webhooks' } },
				})
			}
			if (this.isAdmin) {
				items.push({
					id: 'orgmgmt',
					tag: 'div',
					label: 'settings_option_organization',
					icon: UsersGearIcon,
					iconProps: { type: 'solid' },
					class: { spacer: true },
					onClick: () => this.openOrgManagement(),
				})
			}
			return items.concat(
				{
					id: 'feedback',
					tag: 'router-link',
					label: 'settings_option_feedback',
					icon: PenToSquareIcon,
					iconProps: { type: 'solid' },
					props: { to: { name: 'feedback' } },
				},
				{
					id: 'bugs',
					tag: 'router-link',
					label: 'settings_option_bug',
					icon: BugIcon,
					iconProps: { type: 'solid' },
					props: { to: { name: 'bugreport' } },
				},
				{
					id: 'diagnostics',
					tag: 'router-link',
					label: 'Diagnostics & Troubleshooting',
					icon: GuageHighIcon,
					iconProps: { type: 'solid' },
					class: { spacer: true },
					props: { to: { name: 'diagnostics' } },
				},
				{
					id: 'privacy',
					tag: 'a',
					label: 'settings_option_privacypolicy',
					icon: FileLinesIcon,
					iconProps: { type: 'solid' },
					isExternal: true,
					props: { href: 'https://www.pronto.io/privacy', target: '_blank' },
				},
				{
					id: 'tos',
					tag: 'a',
					label: 'settings_option_termsofservice',
					icon: FileLinesIcon,
					iconProps: { type: 'solid' },
					isExternal: true,
					props: { href: 'https://www.pronto.io/user-terms', target: '_blank' },
				}
			)
		},
	},
	methods: {
		openOrgManagement() {
			EventBus.$emit('openPopout', window.location.origin + '/organization')
		},

		closeDnDModal(data) {
			if (
				(!data.success && !store.auth.user.mute) ||
				(data.success && !store.auth.user.mute)
			) {
				this.dndChecked = false
			}
			this.showDnDModal = false
		},

		hasAdminAccess() {
			this.isAdmin =
				store.auth.user.role === 'admin' || store.auth.user.role === 'support'
		},

		logout() {
			EventBus.$emit('logoutUser')
		},

		goBack() {
			if (this.store.lastRoute) {
				this.$router.push(this.store.lastRoute)
			} else {
				redirectToLastActiveGroup()
			}
		},

		turnOffDnd() {
			let payload = {
				mute: 0,
				muteuntil: null,
			}

			post('/api/v1/user.update', payload)
				.then(({ data }) => {
					data.user.blocked = this.store.auth.user.blocked
					this.store.auth.user = data.user
					this.store.auth.setUser(data.user, this.store.userKey)
				})
				.catch((error) => {
					this.dndChecked = true
					this.$alert.error(i18n.global.t('common_api_error_save'))
					this.isUpdating = false
				})
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

#settingsHeader {
	text-align: center;
	margin-top: -2px;
}

#settingsWrapper {
	padding-top: 32px;
	padding-bottom: 32px;
	z-index: 1;
	top: 0px;
	bottom: 0px;
	left: 280px;
	right: 0;
	position: absolute;
	background-color: var(--neutral-0);
	overflow-y: auto;
}

#divLogOut {
	margin-top: 75px;
	text-align: center;
}
#divLogOut button {
	margin-left: 0;
}

#divVersion {
	@include flex-column-center(8px);
	color: var(--neutral-300);
	margin-top: 40px;
	width: 200px;
	margin: 40px auto 0 auto;
	transition: color ease-in-out 300ms;
	overflow: hidden;
	.pronto-icon {
		font-size: 200px;
		height: 40px;
	}
	.build-number {
		text-align: center;
		font-size: 16px;
	}
}
#divVersion img {
	height: 35px;
	width: 37px;
}

.close-settings {
	position: absolute;
	top: 16px;
	left: 16px;
	font-size: 32px;
	cursor: pointer;
	z-index: 1;
	outline: 0;
	margin: 4px 0 -6px 0;
	color: var(--neutral-400);
	display: none;
	&:hover {
		color: var(--neutral-500);
	}
	@media screen and (max-width: 800px) {
		display: block;
	}
}

.first-row:focus {
	outline: 0;
}

.padlock-icon {
	position: absolute;
	right: 10px;
	top: 28px;
	z-index: 1;
	height: 15px;
}

.announcements-banner-settings-open {
	transition-duration: 400ms;
	transition-delay: 1200ms;
	transition-timing-function: ease-in-out;
	transition-property: all;
}

.announcements-banner-settings-close {
	top: 0 !important;
}

.user-role-container {
	text-align: center;
	margin-bottom: 25px;
	width: 100%;
}

.user-admin-badge {
	padding: 2px 10px 2px 10px;
	border-radius: 5px;
	background-color: transparent;
	border: solid 2px #454959;
	font-family: Avenir-Heavy;
	font-size: 14px;
	font-style: normal;
	font-stretch: normal;
	line-height: normal;
	letter-spacing: normal;
	color: #454959;
	text-align: center;
	display: inline-block;
}

.user-manager-badge {
	color: var(--blue-300);
	border: solid 2px var(--blue-300);
	max-width: 82.5px;
}

.settings-list {
	padding: 0 32px;
	margin: 0 auto 0 auto;
	width: 100%;
	max-width: 900px;
	padding-left: 50px !important;
	padding-right: 50px !important;

	.main-settings-nav-item {
		@include flex-spread;
		text-decoration: none;
		width: 100%;
		height: 72px;
		border-bottom: solid 1px var(--neutral-200);
		&:not(.no-hover) {
			cursor: pointer;
			&:hover {
				.main-settings-nav-content {
					color: var(--teal-300);
					.main-settings-nav-icon {
						color: var(--teal-300);
					}
				}
			}
		}
		&.spacer {
			margin-bottom: 32px;
		}
		// Webhooks glyph reads small at the shared 18px — bump only this one.
		&.nav-item-webhooks .main-settings-nav-content .main-settings-nav-icon {
			font-size: 22px;
		}
		.main-settings-nav-content {
			@include flex;
			color: var(--neutral-800);
			.main-settings-nav-icon {
				color: var(--neutral-400);
				margin-right: 16px;
				font-size: 18px;
				&.active {
					color: var(--blue-300);
				}
			}
		}
		.main-settings-nav-chevron {
			color: var(--neutral-400);
		}
	}
}

.no-hover {
	cursor: default;
}

.profilepic {
	margin: 0 auto;
}

.dnd-controls {
	@include flex;
	color: var(--neutral-600);
	&.active {
		color: var(--blue-300);
	}
	.dnd-state {
		cursor: pointer;
		font-size: 14px;
		margin-right: 15px;
		color: inherit;
	}
	.pronto-toggle {
		font-size: 28px;
	}
}

.profile-section {
	display: block;
	text-decoration: none;
	text-align: center;
	padding: 20px;
	border-radius: 6px;
	cursor: pointer;
	color: var(--neutral-800);
	&:hover {
		background: var(--neutral-100);
	}
}

.profile-container {
	position: relative;
	.user-pronoun-container {
		display: flex;
		justify-content: center;
		margin: 5px 0 7px 0;
		.user-pronoun {
			background: var(--neutral-600);
			border-radius: 6px;
			font-family: Avenir-Heavy;
			font-size: 12px;
			color: var(--neutral-0);
			padding: 5px 8px;
			max-width: max-content;
			text-align: center;
		}
	}
	.user-title {
		font-family: Avenir-Heavy;
		font-size: 14px;
		color: var(--neutral-500);
		margin: 5px 0 5px 0;
	}
}

.profile-name {
	font-family: Avenir-Heavy;
	font-size: 22px;
}

.organization-name {
	font-family: Avenir-Heavy;
	font-size: 14px;
	color: var(--neutral-500);
}

.edit-icon {
	position: absolute;
	top: 75px;
	right: 25px;
}

.profile-pic-input {
	display: none;
}

.settings-list a:hover:not(.no-hover) {
	.title {
		color: var(--teal-300);
	}

	.chevron {
		color: var(--teal-300);
	}

	:deep(.icon) g {
		fill: var(--teal-300);
	}
}

.clear-storage-content {
	padding: 24px;
	@include flex-column-center;
	.trash {
		color: var(--red-200);
		font-size: 40px;
	}
	> p {
		font-size: 12px;
		text-align: center;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	#settingsWrapper {
		background-color: var(--neutral-100);

		.main-settings-nav-item {
			&:not(.no-hover) {
				&:hover {
					background: var(--neutral-0);
				}
			}
			.main-settings-nav-icon {
				&.active {
					color: var(--teal-300);
				}
			}
		}

		.dnd-controls {
			&.active {
				color: var(--teal-300);
			}
		}

		.profile-section {
			&:hover {
				background: var(--neutral-200);
			}
		}
	}
}
</style>
